import { defineStore } from 'pinia';
import { configRequest } from 'config-request';
import { CategoryCode } from 'config-request/src/interfaces/feature-show-all';
import { computed, ref } from 'vue';
// eslint-disable-next-line import/no-cycle
import { useGlobalStore } from './global';
import { getRequestIns } from '../requestIns';
import { debounceFn, isEqual } from '../utils';
export const usePriceListStore = defineStore('price-list', () => {
    const globalData = useGlobalStore();
    const data = ref(null);
    const priceLoading = ref(false);
    const priceLoadOver = ref(false);
    let showPriceLoading = ref(false);
    let lastOption = {};
    let lastGenerationCode = null;
    /**
     * 总价
     */
    const totalPrice = computed(() => {
        return data.value?.total_price;
    });
    /**
     * 组合包入口文案
     */
    const selectOptionalPackTip = computed(() => {
        return data.value?.select_optional_pack_tip;
    });
    /**
     * 套装入口文案
     */
    const selectSpecialPackTip = computed(() => {
        return data.value?.select_special_pack_tip;
    });
    /**
     * 价格明细
     */
    const deduction = computed(() => {
        if (!data.value?.category_list)
            return null;
        const deductionItem = data.value.category_list.find((category) => {
            return category.code === CategoryCode.Deduction;
        });
        if (!deductionItem)
            return null;
        const barList = deductionItem.option_list.map((optionItem) => {
            const barItem = {
                configName: optionItem.name,
                desc: optionItem.desc || '',
                price: optionItem.price || 0,
                price_prefix_desc: optionItem.price_prefix_desc,
                plus_minus_desc: optionItem.plus_minus_desc,
                tip_desc: optionItem.tip_desc,
                tip_url: optionItem.tip_url,
                package_price_desc: optionItem.package_price_desc,
            };
            return barItem;
        });
        const deductionConfig = {
            title: deductionItem.name,
            code: deductionItem.code,
            showRadio: !!deductionItem.order_person_type,
            orderPersonType: deductionItem.order_person_type,
            barList,
        };
        return deductionConfig;
    });
    const configPrice = computed(() => {
        if (!data.value?.category_list)
            return [];
        const optionArray = [];
        data.value.category_list.forEach((category) => {
            if (category.code === CategoryCode.Discount ||
                category.code == CategoryCode.Deduction ||
                category.code == CategoryCode.Gift)
                return;
            const barList = category.option_list.map((optionItem) => {
                const barItem = {
                    configName: optionItem.name,
                    price: optionItem.price || 0,
                    price_prefix_desc: optionItem.price_prefix_desc,
                    plus_minus_desc: optionItem.plus_minus_desc,
                    imgSrc: optionItem.img_url || '',
                    select_discount_tip: optionItem?.select_discount_tip || '',
                    select_discount_tip_icon: optionItem?.select_discount_tip_icon || '',
                    select_discount_tip_icon_ratio: optionItem?.select_discount_tip_icon_ratio || '',
                    origin_price: optionItem.origin_price,
                    discount_icon_v2: optionItem.discount_icon_v2,
                    discount_icon_v2_ratio: optionItem.discount_icon_v2_ratio,
                    package_price_desc: optionItem.package_price_desc,
                };
                return barItem;
            });
            const obj = {
                title: category.name,
                code: category.code,
                barList: barList,
            };
            optionArray.push(obj);
        });
        return optionArray;
    });
    const giftModule = computed(() => {
        if (!data.value?.category_list)
            return null;
        const deductionItem = data.value.category_list.find((category) => {
            return category.code === CategoryCode.Gift;
        });
        if (!deductionItem)
            return null;
        const barList = deductionItem.option_list.map((optionItem) => {
            const barItem = {
                configName: optionItem.name,
                desc: optionItem.desc || '',
                price: optionItem.price || 0,
                price_prefix_desc: optionItem.price_prefix_desc,
                plus_minus_desc: optionItem.plus_minus_desc,
                tip_desc: optionItem.tip_desc,
                tip_url: optionItem.tip_url,
                package_price_desc: optionItem.package_price_desc,
            };
            return barItem;
        });
        const deductionConfig = {
            title: deductionItem.name,
            code: deductionItem.code,
            barList,
        };
        return deductionConfig;
    });
    const formatFeatureList = (priceData) => {
        if (!priceData)
            return;
        const deductionItem = priceData.category_list.find((category) => {
            return category.code === CategoryCode.Deduction;
        });
        if (!deductionItem)
            return null;
        globalData.updateConfig('order_person_type', deductionItem.order_person_type);
    };
    const getPriceList = async (params = {}, { showToast = false } = {}) => {
        let globalStore = useGlobalStore();
        const currentFlatOptions = globalStore.currentFlatOptions;
        // 获取当前选择的所有配置项
        let option_list = [];
        Object.keys(currentFlatOptions).forEach((key) => {
            if (key.startsWith('F')) {
                const item = { feature_code: key, code: currentFlatOptions[key] };
                option_list.push(item);
            }
        });
        let priceParams = {
            option_list,
            generation_code: currentFlatOptions.generation_code,
            model_code: currentFlatOptions.model_code || globalData.currentOptions.model_code,
            order_person_type: currentFlatOptions.order_person_type,
        };
        if (globalStore.newVersion !== undefined) {
            priceParams.new_version = globalStore.newVersion;
        }
        if (globalStore.orderNo) {
            priceParams.order_no = globalStore.orderNo;
        }
        Object.assign(priceParams, params);
        priceLoading.value = true;
        priceLoadOver.value = false;
        try {
            const result = await configRequest(getRequestIns()).priceGet(priceParams, {
                throwError: true,
                showToast,
            });
            priceLoadOver.value = true;
            if (result && result.data) {
                // console.log('---priceGet--->>>', globalData.currentOpts)
                priceLoading.value = false;
                data.value = result.data;
                lastOption = {
                    ...currentFlatOptions,
                };
            }
            return result.data;
        }
        catch (err) {
            priceLoadOver.value = true;
            priceLoading.value = false;
            showPriceLoading.value = false;
            data.value = {
                total_price: -1,
            };
        }
    };
    const subscribeDebounce = debounceFn((...args) => {
        if (!globalData.currentOptions.generation_code ||
            !globalData.currentOptions.model_code)
            return;
        // if (!globalData.toggleGeneration) {
        //   return
        // }
        // console.log(
        //   '---state.currentOpts, lastOption---',
        //   lastOption,
        //   globalData.currentFlatOptions,
        //   isEqual(globalData.currentFlatOptions, lastOption),
        //   globalData.currentOpts.generation_code,
        //   lastGenerationCode
        // )
        if (totalPrice.value !== undefined &&
            totalPrice.value >= 0 &&
            isEqual(lastOption, globalData.currentFlatOptions) &&
            lastGenerationCode === globalData.currentOpts.generation_code)
            return;
        showPriceLoading.value = true;
        getPriceList(...args).finally(() => {
            showPriceLoading.value = false;
            lastGenerationCode = globalData.currentOpts.generation_code;
        });
    });
    const $reset = () => {
        data.value = null;
        priceLoading.value = false;
        priceLoadOver.value = false;
        showPriceLoading.value = false;
        lastOption = {};
        lastGenerationCode = null;
    };
    /**
     * 订阅 配置 store
     */
    globalData.$subscribe(() => subscribeDebounce(), {
        detached: true,
    });
    return {
        data,
        totalPrice,
        deduction,
        configPrice,
        priceLoading,
        priceLoadOver,
        showPriceLoading,
        giftModule,
        selectOptionalPackTip,
        selectSpecialPackTip,
        formatFeatureList,
        getPriceList,
        getPriceListDebounced: subscribeDebounce,
        $reset,
    };
});
